/**
 * KRA Photo Journal – PinPicker
 *
 * Overlay component that lets the user pick an Asakusa Map pin
 * to send selected photos to. Fetches pin data from the Asakusa Map API.
 */

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { fetchMapPins, type MapAreaId } from "../lib/api.js";
import { cn } from "../lib/utils.js";

interface PinPickerProps {
  onSelect: (pinNumber: number, area: MapAreaId) => void;
  onClose: () => void;
  photoCount: number;
}

interface MapPin {
  number: number;
  name: string;
  nameJa: string;
}

const AREAS: Array<{ id: MapAreaId; label: string }> = [
  { id: "sensoji", label: "Asakusa" },
  { id: "sumida-park", label: "Sumida" },
  { id: "west-asakusa", label: "West" },
];

export function PinPicker({ onSelect, onClose, photoCount }: PinPickerProps) {
  const [area, setArea] = useState<MapAreaId>("sensoji");
  const [pins, setPins] = useState<MapPin[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSelected(null);
    fetchMapPins(area)
      .then((data) => {
        if (!cancelled) setPins(data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [area]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="w-[340px] max-h-[70vh] md:w-[640px] md:max-h-[80vh] flex flex-col rounded-2xl bg-[#1a1a2e] border border-white/10 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h3 className="text-white text-base font-semibold">Chọn Pin</h3>
            <p className="text-white/50 text-xs mt-0.5">
              Gửi {photoCount} ảnh tới bản đồ Asakusa
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white/80 transition-colors p-1"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Area tabs */}
        <div className="flex gap-1 px-3 pt-3">
          {AREAS.map((a) => (
            <button
              key={a.id}
              onClick={() => setArea(a.id)}
              className={cn(
                "flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors",
                area === a.id
                  ? "bg-[#5B8A8A] text-white"
                  : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80",
              )}
            >
              {a.label}
            </button>
          ))}
        </div>

        {/* Pin list */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1 md:space-y-0 md:grid md:grid-cols-2 md:gap-1.5">
          {loading ? (
            <div className="flex items-center justify-center py-10 md:col-span-2">
              <div className="w-5 h-5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
            </div>
          ) : pins.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-8 md:col-span-2">
              Không tìm thấy pin nào
            </p>
          ) : (
            pins.map((pin) => (
              <button
                key={pin.number}
                onClick={() => setSelected(pin.number)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left",
                  selected === pin.number
                    ? "bg-[#5B8A8A]/30 border border-[#5B8A8A]/50"
                    : "hover:bg-white/5 border border-transparent"
                )}
              >
                <span
                  className={cn(
                    "flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold",
                    selected === pin.number
                      ? "bg-[#5B8A8A] text-white"
                      : "bg-white/10 text-white/60"
                  )}
                >
                  {pin.number}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {pin.name}
                  </p>
                  {pin.nameJa && (
                    <p className="text-white/40 text-xs truncate">
                      {pin.nameJa}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-white/10 flex items-center gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-white/60 hover:text-white/80 text-sm font-medium transition-colors"
          >
            Huỷ
          </button>
          <button
            onClick={() => selected !== null && onSelect(selected, area)}
            disabled={selected === null}
            className={cn(
              "flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors",
              selected !== null
                ? "bg-[#5B8A8A] hover:bg-[#4d7676] text-white shadow-lg shadow-[#5B8A8A]/20"
                : "bg-white/5 text-white/30 cursor-not-allowed"
            )}
          >
            Gửi tới Pin {selected ?? "…"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
